import React from "react";
import { Page, View, Text } from "@react-pdf/renderer";

import getPDFTranslations from "utils/translation/szc-pdf";
import { APP_VERSION } from "utils/constants/version";

import { styles, COLORS } from "../Document/pdf-layout/pdfStyles";
import { Row, NumField, SmallTitle, Field, Checkbox, ValueBox, SplitCell, LineField, ItemField } from "../Document/pdf-layout/pdfPrimitives";
import { TitleBar, Footer, DisclaimerProjectRow } from "../Document/pdf-layout/pdfShared";

// Page 1 of the SZC report
const PDF_Export_First_Page = ({
    language = "en",
    modelName = "",
    inputFieldsData = {},
    displayData,
    calcPerformedBy = {},
    displayUnits: units = "Metric",
}) => {
    const {
        issuedForTheUseOf = "",
        projectNo = "",
        lot = "",
        site = "",
        email: emailFromForm = "",
        attachedDocsNotes = [],
    } = inputFieldsData;

    const {
        buildingLocation: {
            cityProvince = "",
            postalCode = "",
            address = "",
        },
        compliance: {
            designatedRooms = [],
            minCoolingCapacity = 0,
        },
        designConditions: {
            cooling: {
                outdoorTemp = 0,
                indoorTemp = 0,
                adjacentTemp = 0,
            },
            weatherLocation = "",
            sczVentilationAirFlowRate,
            hrv,
            hrvAtre,
            area,
        },
        outsideFacingAssemblies = [],
        adjacentSpacesAssemblies = [],
    } = displayData;

    const {
        cityProvince: userCityProvince = "",
        company = "",
        companyAddress = "",
        companyFax = "",
        companyPhone = "",
        companyPostalCode = "",
        email = "",
        userName = "",
    } = calcPerformedBy;

    const { t } = getPDFTranslations(language);

    const totalPages = 1;

    return (
        <Page size="LETTER" style={styles.page}>
            {/* wrap={false}: the form is designed to fill exactly one page, so keep
                it as one unbreakable block. Without this, content that lands a hair
                over the page height pushes just the footer onto a near-empty extra
                page instead of staying put. */}
            <View style={styles.body} wrap={false}>
                <TitleBar
                    title={t("SZCpdf.title", "B.C. Single Zone Cooling Capacity [!!]")}
                    subtitle={t(
                        "SZCpdf.subtitle",
                        "BCBC 9.33.3.1.; 9.33.5.1. [!!]"
                    )}
                    rightPrimary={t("SZCpdf.standardForm.1", "B.C. SZCG [!!]")}
                    rightSecondary={t("SZCpdf.standardForm.2", "Form Set Ver 1.0a [!!]")}
                />

                {/* Disclaimer + project number */}
                <DisclaimerProjectRow t={t} issuedForTheUseOf={issuedForTheUseOf} projectNo={projectNo} />

                <SmallTitle>{t("SZCpdf.buildingLoc", "BUILDING LOCATION [!!]")}</SmallTitle>
                <Row>
                    <Field
                        label={t("SZCpdf.model", "Model: [!!]")}
                        value={modelName}
                        num="3"
                        containerStyle={styles.borderRight}
                    />
                    <Field label={t("SZCpdf.site", "Site: [!!]")} value={site} num="6" />
                </Row>
                <Row>
                    <Field
                        label={t("SZCpdf.address", "Address: [!!]")}
                        value={address}
                        num="4"
                        containerStyle={styles.borderRight}
                    />
                    <Field label={t("SZCpdf.lot", "Lot: [!!]")} value={lot} num="7" />
                </Row>
                <Row>
                    <Field
                        label={t("SZCpdf.city&Province", "City & Province: [!!]")}
                        value={cityProvince}
                        num="5"
                        containerStyle={styles.borderRight}
                    />
                    <Field label={t("SZCpdf.postalCode", "Postal code: [!!]")} value={postalCode} num="8" />
                </Row>

                <SmallTitle>
                    {t("SZCpdf.coolingZoneCompliance", "COOLING ZONE COMPLIANCE [!!]")}
                </SmallTitle>
                <Row>
                    <Field
                        label={t("SZCpdf.designatedRooms", "Designated Room(s): [!!]")}
                        value={designatedRooms.join(", ")}
                        num="9a"
                        containerStyle={styles.borderRight}
                    />
                    <View style={[styles.fieldCell, { flexDirection: "row", alignItems: "center" }]}>
                        <Text style={[styles.smallLabel, { width: 70, textAlign: "center" }]}>
                            {t("SZCpdf.units", "Units: [!!]")}
                        </Text>
                        <View style={styles.selectionBoxContainer}>
                            <Checkbox checked={units === "Imperial"} label={t("SZCpdf.imperial", "Imperial [!!]")} />
                            <Checkbox checked={units === "Metric"} label={t("SZCpdf.metric", "Metric [!!]")} />
                        </View>
                        <NumField>b</NumField>
                    </View>
                </Row>
                <View
                    style={{
                        borderBottomWidth: 1,
                        borderColor: "#000",
                        borderStyle: "solid",
                        flexGrow: 1,
                        justifyContent: "space-around",
                    }}
                >
                    <View style={styles.textWithBoxContainer}>
                        <Text style={styles.textWithBox}>
                            {t("SZCpdf.minCoolingCap", "Minimum Cooling Capacity: [!!]")}
                        </Text>
                        <ValueBox value={parseFloat(minCoolingCapacity).toFixed(0)} unit={units === "Metric" ? "W" : "Btu/h"} />
                        <NumField style={{ right: -6 }}>c</NumField>
                    </View>
                </View>

                <SmallTitle>{t("SZCpdf.designConditions", "DESIGN CONDITIONS [!!]")}</SmallTitle>
                <View>
                    <Row>
                        <SplitCell style={styles.borderRight}>
                            <Field
                                label={t("SZCpdf.outdoorTemp", "Outdoor Temp: [!!]")}
                                value={outdoorTemp}
                                num="10"
                                containerStyle={styles.borderRight}
                            />
                            <Field
                                label={t("SZCpdf.indoorTemp", "Indoor Temp: [!!]")}
                                value={indoorTemp}
                                num="11"
                            />
                        </SplitCell>
                        <SplitCell>
                            <Field
                                label={t("SZCpdf.adjacentTemp", "Adjacent Temp: [!!]")}
                                value={adjacentTemp}
                                num="12"
                                containerStyle={styles.borderRight}
                            />
                            <Field
                                label={t("SZCpdf.coolingZoneArea", "Cooling Zone Area: [!!]")}
                                value={area}
                                num="13"
                                labelStyle={styles.smallLabel}
                            />
                        </SplitCell>
                    </Row>
                    <Row>
                        <SplitCell style={styles.borderRight}>
                            <Field
                                label={t("SZCpdf.weatherLocation", "Weather location: [!!]")}
                                value={weatherLocation}
                                num="14"
                                containerStyle={styles.borderRight}
                            />
                            <Field
                                label={t("SZCpdf.ventilationRate", "Ventilation Rate: [!!]")}
                                value={sczVentilationAirFlowRate}
                                num="15"
                            />
                        </SplitCell>
                        <SplitCell>
                            <Field
                                label={t("SZCpdf.hrv", "HRV/ERV?: [!!]")}
                                value={hrv}
                                num="16"
                                containerStyle={styles.borderRight}
                            />
                            <Field
                                label={t("SZCpdf.atre", "ATRE %: [!!]")}
                                value={hrvAtre}
                                num="17"
                                labelStyle={styles.smallLabel}
                            />
                        </SplitCell>
                    </Row>
                </View>

                <SmallTitle>{t("SZCpdf.buildingCompsOutside", "BUILDING COMPONENTS & ASSEMBLIES FACING OUTSIDE [!!]")}</SmallTitle>
                <Row>
                    <ItemField letter="A" value={outsideFacingAssemblies[0] || ""} num="18" half borderRight t={t} />
                    <ItemField letter="G" value={outsideFacingAssemblies[6] || ""} num="24" half t={t} />
                </Row>
                <Row>
                    <ItemField letter="B" value={outsideFacingAssemblies[1] || ""} num="19" half borderRight t={t} />
                    <ItemField letter="H" value={outsideFacingAssemblies[7] || ""} num="25" half t={t} />
                    
                </Row>
                <Row>
                    <ItemField letter="C" value={outsideFacingAssemblies[2] || ""} num="20" half borderRight t={t} />
                    <ItemField letter="I" value={outsideFacingAssemblies[8] || ""} num="26" half t={t} />
                    
                </Row>
                <Row>
                    <ItemField letter="D" value={outsideFacingAssemblies[3] || ""} num="21" half borderRight t={t} />
                    <ItemField letter="J" value={outsideFacingAssemblies[9] || ""} num="27" half t={t} />
                </Row>
                <Row>
                    <ItemField letter="E" value={outsideFacingAssemblies[4] || ""} num="22" half borderRight t={t} />
                    <ItemField letter="K" value={outsideFacingAssemblies[10] || ""} num="28" half t={t} />
                </Row>
                <Row>
                    <ItemField letter="F" value={outsideFacingAssemblies[5] || ""} num="23" half borderRight t={t} />
                    <ItemField letter="L" value={outsideFacingAssemblies[11] || ""} num="29" half t={t} />
                </Row>

                <SmallTitle>{t("SZCpdf.buildingCompsAdjacentSpaces", "BUILDING COMPONENTS & ASSEMBLIES ADJACENT SPACES [!!]")}</SmallTitle>
                <Row>
                    <ItemField letter="A" value={adjacentSpacesAssemblies[0] || ""} num="18" half borderRight t={t} />
                    <ItemField letter="G" value={adjacentSpacesAssemblies[6] || ""} num="24" half t={t} />
                </Row>
                <Row>
                    <ItemField letter="B" value={adjacentSpacesAssemblies[1] || ""} num="19" half borderRight t={t} />
                    <ItemField letter="H" value={adjacentSpacesAssemblies[7] || ""} num="25" half t={t} />
                    
                </Row>
                <Row>
                    <ItemField letter="C" value={adjacentSpacesAssemblies[2] || ""} num="20" half borderRight t={t} />
                    <ItemField letter="I" value={adjacentSpacesAssemblies[8] || ""} num="26" half t={t} />
                    
                </Row>
                <Row>
                    <ItemField letter="D" value={adjacentSpacesAssemblies[3] || ""} num="21" half borderRight t={t} />
                    <ItemField letter="J" value={adjacentSpacesAssemblies[9] || ""} num="27" half t={t} />
                </Row>
                <Row>
                    <ItemField letter="E" value={adjacentSpacesAssemblies[4] || ""} num="22" half borderRight t={t} />
                    <ItemField letter="K" value={adjacentSpacesAssemblies[10] || ""} num="28" half t={t} />
                </Row>
                <Row>
                    <ItemField letter="F" value={adjacentSpacesAssemblies[5] || ""} num="23" half borderRight t={t} />
                    <ItemField letter="L" value={adjacentSpacesAssemblies[11] || ""} num="29" half t={t} />
                </Row>

                {/* Attached documents */}
                <SmallTitle>{t("SZCpdf.attachedDocsNotes", "ATTACHED DOCUMENTS & NOTES [!!]")}</SmallTitle>
                <Row>
                    <LineField number="1" value={attachedDocsNotes?.[0] || ""} num="42" t={t} />
                </Row>
                <Row>
                    <LineField number="2" value={attachedDocsNotes?.[1] || ""} num="43" t={t} />
                </Row>
                <Row>
                    <LineField number="3" value={attachedDocsNotes?.[2] || ""} num="44" t={t} />
                </Row>
                <Row>
                    <LineField number="4" value={attachedDocsNotes?.[3] || ""} num="45" t={t} />
                </Row>
                <Row>
                    <LineField number="5" value={attachedDocsNotes?.[4] || ""} num="46" t={t} />
                </Row>

                {/* Calculations performed by */}
                <SmallTitle>{t("SZCpdf.calculationsBy", "CALCULATIONS PERFORMED BY [!!]")}</SmallTitle>
                {/* minHeight matters: the basis-0 field cells report ~zero natural height
                    to Yoga, so without a floor this row loses the grow split against the
                    heating/cooling sections and its content overflows onto the footer. */}
                <Row style={[styles.fillRow, { minHeight: 180 }]}>
                    <View style={styles.calcLeftPair}>
                        <View style={styles.columnA}>
                            <Field
                                label={t("SZCpdf.name", "Name: [!!]")}
                                value={userName}
                                num="47"
                                containerStyle={styles.borderBottom}
                            />
                            <Field
                                label={t("SZCpdf.company", "Company: [!!]")}
                                value={company}
                                num="48"
                                containerStyle={styles.borderBottom}
                            />
                            <Field
                                label={t("SZCpdf.address", "Address: [!!]")}
                                value={companyAddress}
                                num="49"
                                containerStyle={styles.borderBottom}
                            />
                            <Field
                                label={t("SZCpdf.city&Province", "City & Province: [!!]")}
                                value={userCityProvince}
                                num="50"
                                containerStyle={styles.borderBottom}
                            />
                            <Field
                                label={t("SZCpdf.postalCode", "Postal code: [!!]")}
                                value={companyPostalCode}
                                num="51"
                                containerStyle={styles.borderBottom}
                            />
                            <Field
                                label={t("SZCpdf.telephone", "Telephone: [!!]")}
                                value={companyPhone}
                                num="52"
                                containerStyle={styles.borderBottom}
                            />
                            <Field
                                label={t("SZCpdf.fax", "Fax: [!!]")}
                                value={companyFax}
                                num="53"
                                containerStyle={styles.borderBottom}
                            />
                            <Field
                                label={t("SZCpdf.email", "E-mail: [!!]")}
                                value={emailFromForm || email}
                                num="54"
                                labelStyle={{ width: 30 }}
                            />
                        </View>
                        <View style={styles.stampBox}>
                            <NumField>55</NumField>
                        </View>
                    </View>
                    <View style={styles.columnPlain}>
                        <View style={[styles.cellSmallPadding, styles.borderBottom, { minHeight: 60, padding: 6 }]}>
                            <Text style={{ fontSize: 6, lineHeight: 1.4 }}>
                                {t("SZCpdf.attestation.1", "I,")}{" "}
                                <Text style={{ color: COLORS.blue, fontFamily: "Helvetica-Bold", fontSize: 7 }}>
                                    {userName}
                                </Text>{" "}
                                {t(
                                    "SZCpdf.attestation.2",
                                    ", have reviewed and take responsibility for the design work described in this document & I am qualified in the appropriate categories. [!!]"
                                )}
                            </Text>
                            <NumField>56</NumField>
                        </View>
                        <Field
                            label={`${t("SZCpdf.accredRef.1", "Accreditation [!!]", { num: 1 })}\n${t("SZCpdf.accredRef.2", "Reference 1: [!!]", { num: 1 })}`}
                            value=""
                            num="57"
                            containerStyle={[styles.borderBottom, { minHeight: 26 }]}
                        />
                        <Field
                            label={`${t("SZCpdf.accredRef.1", "Accreditation [!!]", { num: 2 })}\n${t("SZCpdf.accredRef.2", "Reference 2: [!!]", { num: 2 })}`}
                            value=""
                            num="58"
                            containerStyle={[styles.borderBottom, { minHeight: 26 }]}
                        />
                        <Field
                            label={t("SZCpdf.issuedFor", "Issued Date & Purpose: [!!]")}
                            value=""
                            num="59"
                            containerStyle={styles.borderBottom}
                        />
                        <Field
                            label={t("SZCpdf.reIssuedFor", "Re-Issued Date & Purpose: [!!]")}
                            value=""
                            num="60"
                            containerStyle={styles.borderBottom}
                        />
                        <View
                            style={[
                                styles.cellSmallPadding,
                                { alignItems: "center", justifyContent: "center", minHeight: 16 },
                            ]}
                        >
                            <Text style={{ fontFamily: "Helvetica-Bold", fontSize: 7 }}>
                                {t("SZCpdf.page", `Page: 1 of 1 [!!]`, { current: 1, total: totalPages })}
                            </Text>
                        </View>
                    </View>
                </Row>

                <Footer
                    footnote={t("SZCpdf.footnote", `Version ${APP_VERSION}. Contact: info@voltaresearch.org [!!]`, {
                        version: APP_VERSION,
                    })}
                    num="61"
                />
            </View>
        </Page>
    );
};

export default PDF_Export_First_Page;
